import { CheckCircle2Icon, CircleAlertIcon, InfoIcon, Loader2Icon, XIcon } from "lucide-react";
import { useSyncExternalStore } from "react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Progress } from "./progress";

/**
 * Powiadomienia przejściowe („toast").
 *
 * Powstały ze zgłoszenia z 12.10.2026: po dołączeniu Shinden zebranie kopii
 * odcinka trwa do pół minuty, a kliknięcie „Odtwórz" nie dawało przez ten czas
 * żadnego znaku poza słowem w przycisku. Druga rzecz, którą to naprawia: błędy
 * listy odcinków lądowały w ramce POD siatką — przy dwudziestu czterech odcinkach
 * poza ekranem, czyli nigdzie.
 *
 * Magazyn jest modułem, nie kontekstem Reacta, bo komunikat wywołuje się z miejsc,
 * które drzewa nie znają (obsługa żądania, śledzenie pobierania), a jedna warstwa
 * `<Toaster />` w powłoce rysuje wszystko. Ten sam `id` PODMIENIA treść zamiast
 * dokładać drugi komunikat — tak jeden toast prowadzi odcinek przez kolejne etapy.
 */

export type ToastTone = "info" | "loading" | "success" | "error";

export interface ToastOptions {
  /** Stały klucz: ten sam `id` podmienia treść zamiast dokładać drugi komunikat. */
  id?: string;
  title: string;
  description?: string;
  tone?: ToastTone;
  /**
   * Postęp 0–100 jako cienki pasek pod treścią. `null` rysuje pasek nieokreślony,
   * brak pola — żadnego paska.
   */
  progress?: number | null;
  /**
   * Czas do samoczynnego zniknięcia. `null` — do zamknięcia albo podmiany.
   * Domyślnie 5 s; komunikat w toku (`loading`) domyślnie zostaje, bo zniknięcie
   * w trakcie pracy wyglądałoby jak jej koniec.
   */
  durationMs?: number | null;
  action?: { label: string; onClick: () => void };
  /** Wołane wyłącznie wtedy, gdy widz zamknie komunikat sam. */
  onDismiss?: () => void;
}

interface ToastEntry extends ToastOptions {
  id: string;
  tone: ToastTone;
}

/** Ile komunikatów naraz. Więcej przestaje być powiadomieniem, a zaczyna listą. */
const MAX_VISIBLE = 4;
const DEFAULT_DURATION_MS = 5000;

let entries: ToastEntry[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
let counter = 0;

function publish(next: ToastEntry[]): void {
  entries = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Pokazuje komunikat albo podmienia ten o tym samym `id`. Zwraca `id`. */
export function toast(options: ToastOptions): string {
  counter += 1;
  const id = options.id ?? `toast-${counter}`;
  const entry: ToastEntry = { ...options, id, tone: options.tone ?? "info" };
  const exists = entries.some((e) => e.id === id);
  publish(exists ? entries.map((e) => (e.id === id ? entry : e)) : [...entries, entry]);

  const previous = timers.get(id);
  if (previous !== undefined) clearTimeout(previous);
  timers.delete(id);
  const duration =
    options.durationMs !== undefined
      ? options.durationMs
      : entry.tone === "loading"
        ? null
        : DEFAULT_DURATION_MS;
  if (duration !== null) {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), duration),
    );
  }
  return id;
}

/** Zdejmuje komunikat. `byUser` woła `onDismiss` — zamknięcie ręczne to decyzja. */
export function dismissToast(id: string, byUser = false): void {
  const entry = entries.find((e) => e.id === id);
  if (entry === undefined) return;
  const timer = timers.get(id);
  if (timer !== undefined) clearTimeout(timer);
  timers.delete(id);
  publish(entries.filter((e) => e.id !== id));
  if (byUser) entry.onDismiss?.();
}

/** Wyłącznie dla testów. */
export function _toastsForTests(): readonly ToastEntry[] {
  return entries;
}

const ICONS: Record<ToastTone, typeof InfoIcon> = {
  info: InfoIcon,
  loading: Loader2Icon,
  success: CheckCircle2Icon,
  error: CircleAlertIcon,
};

const ICON_TONE: Record<ToastTone, string> = {
  info: "text-accent",
  loading: "animate-spin text-primary",
  success: "text-success",
  error: "text-destructive",
};

/**
 * Warstwa komunikatów — jedna, w powłoce aplikacji.
 *
 * Na dotyku stoi NAD paskiem dolnym (ten sam odstęp co dół treści), bo pod nim
 * kciuk by go nie sięgnął, a pasek by go zasłonił. Na szerokim ekranie w prawym
 * dolnym rogu, z dala od szyny nawigacji.
 */
export function Toaster() {
  const items = useSyncExternalStore(
    subscribe,
    () => entries,
    () => entries,
  );
  const visible = items.slice(-MAX_VISIBLE);

  return (
    <section
      aria-label="Powiadomienia"
      className={cn(
        "pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2",
        "bottom-[calc(4.75rem+env(safe-area-inset-bottom))] px-[var(--page-gutter)]",
        "md:inset-x-auto md:right-6 md:bottom-6 md:items-end md:px-0",
      )}
    >
      <ol aria-live="polite" className="flex w-full flex-col items-center gap-2 md:items-end">
        {visible.map((item) => {
          const Icon = ICONS[item.tone];
          return (
            <li
              key={item.id}
              data-slot="toast"
              className={cn(
                "pointer-events-auto flex w-full max-w-sm flex-col gap-2 rounded-xl border bg-popover/95 p-3",
                "text-popover-foreground shadow-xl shadow-black/40 backdrop-blur-xl",
                item.tone === "error" ? "border-destructive/40" : "border-border",
              )}
            >
              <div className="flex items-start gap-3">
                <Icon className={cn("mt-0.5 size-5 shrink-0", ICON_TONE[item.tone])} aria-hidden />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="truncate text-sm font-semibold">{item.title}</p>
                  {item.description !== undefined && (
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mt-1 -mr-1 size-7 shrink-0 text-muted-foreground"
                  aria-label="Zamknij powiadomienie"
                  onClick={() => dismissToast(item.id, true)}
                >
                  <XIcon />
                </Button>
              </div>

              {item.progress !== undefined && (
                <Progress value={item.progress} className="h-1" aria-label={item.title} />
              )}

              {item.action !== undefined && (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      item.action?.onClick();
                      dismissToast(item.id);
                    }}
                  >
                    {item.action.label}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
